import { useMemo } from "react";
import isNil from "lodash/isNil";
import { Rule } from "interfaces/rule";
import { useFetchGetV2 } from "hooks/fetch/useFetchGetV2";

/**
 * @property {boolean} loading whether the rule is currently being fetched
 * @property {boolean} error whether an error occurred while fetching the rule
 * @property {Rule | undefined} rule the rule object or undefined, defaults to undefined while loading or when an error occurs
 * @property {() => Promise<Rule>} refetchRule callback function for triggering a refetch of the rule, the new rule can be accessed via the hook or via the response of the callback
 */
interface RuleResponse {
  loading: boolean;
  error: boolean;
  rule: Rule | undefined;
  refetchRule: () => Promise<Rule>;
}

/**
 * Hook for fetching a rule from the AIHub backend
 * @param id the id of the rule to fetch
 * @returns {RuleResponse} the response object containing the status info, the rule object if fetched and a refetch callback
 */
export const useGetRule = (id?: string | number | null): RuleResponse => {
  const url = useMemo(() => `read_rules/${id}`, [id]);
  const { loading, error, response, refetch: refetchRule } = useFetchGetV2(
    url,
    isNil(id)
  );

  return {
    loading,
    error,
    rule: response,
    refetchRule
  };
};
